import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Image3D } from '~/domain/patient/image3d';
import { Patient } from '~/domain/patient/patient';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { CopyTarget } from './types';
import { useTargetImageSelection } from './useTargetImageSelection';
import { useRoiFilterAndSelection } from './useRoiFilterAndSelection';
import TargetImageSelectionPanel from './TargetImageSelectionPanel';
import StructureFilterPanel from './StructureFilterPanel';
import StructureListPanel from './StructureListPanel';

type CopyStructureSetDialogProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: (target: CopyTarget) => Promise<void>;
    sourceStructureSet: StructureSetReadModel;
    patient: Patient;
    sourceImage: Image3D;
    initialSelectedRoiNumbers?: ReadonlySet<number>;
};

export default function CopyStructureSetDialog({
    open,
    onClose,
    onConfirm,
    sourceStructureSet,
    patient,
    sourceImage,
    initialSelectedRoiNumbers,
}: CopyStructureSetDialogProps) {
    const intl = useIntl();
    const [isProcessing, setIsProcessing] = useState(false);
    const [overwriteExisting, setOverwriteExisting] = useState(false);

    const targetSelection = useTargetImageSelection(
        patient,
        sourceImage,
        sourceStructureSet
    );

    const roiSelection = useRoiFilterAndSelection(
        sourceStructureSet,
        targetSelection.activeImageTargetSs,
        overwriteExisting,
        initialSelectedRoiNumbers
    );
    const canConfirm = useMemo(() => {
        if (roiSelection.selectedRois.size === 0) {
            return false;
        }
        if (!targetSelection.selectedTarget) {
            return false;
        }
        if (!targetSelection.selectedImageSsUid) {
            return false;
        }
        if (
            !targetSelection.selectedTarget.isSourceImage &&
            !targetSelection.selectedTarget.geometryMatch &&
            !targetSelection.selectedTarget.sameFrameOfReference &&
            !targetSelection.activeRegistration
        ) {
            return false;
        }
        return true;
    }, [
        roiSelection.selectedRois.size,
        targetSelection.selectedTarget,
        targetSelection.selectedImageSsUid,
        targetSelection.activeRegistration,
    ]);

    const handleConfirm = useCallback(async () => {
        if (!targetSelection.selectedTarget) {
            return;
        }
        const target: CopyTarget = {
            image: targetSelection.selectedTarget.image,
            selectedRoiNumbers: roiSelection.selectedRois,
            registration: targetSelection.activeRegistration ?? undefined,
            targetStructureSet:
                targetSelection.activeImageTargetSs ?? undefined,
            overwriteExisting,
            sameFrameOfReference:
                targetSelection.selectedTarget.sameFrameOfReference,
        };
        setIsProcessing(true);
        try {
            await onConfirm(target);
        } finally {
            setIsProcessing(false);
        }
    }, [
        targetSelection.selectedTarget,
        targetSelection.activeImageTargetSs,
        targetSelection.activeRegistration,
        roiSelection.selectedRois,
        overwriteExisting,
        onConfirm,
    ]);

    const selectedCount = roiSelection.selectedRois.size;

    return (
        <Dialog
            open={open}
            onClose={isProcessing ? undefined : onClose}
            maxWidth="md"
            fullWidth
            data-testid="copy-structure-set-dialog"
            PaperProps={{ elevation: 0 }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    px: 2,
                }}
            >
                <Typography
                    variant="h6"
                    component="span"
                    fontSize={15}
                    fontWeight={500}
                >
                    <FormattedMessage
                        id="structureSet.copy.title"
                        defaultMessage="Copy Structures"
                    />
                </Typography>
                <IconButton
                    size="small"
                    onClick={onClose}
                    aria-label={intl.formatMessage({ id: 'close' })}
                    disabled={isProcessing}
                >
                    <X size={16} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 0, display: 'flex', height: 540 }}>
                <TargetImageSelectionPanel
                    targetImages={targetSelection.targetImages}
                    selectedImageSeriesUid={
                        targetSelection.selectedImageSeriesUid
                    }
                    selectedImageSsUid={targetSelection.selectedImageSsUid}
                    availableRegistrations={
                        targetSelection.availableRegistrations
                    }
                    selectedRegistrationUid={
                        targetSelection.selectedRegistrationUid
                    }
                    sourceImage={sourceImage}
                    onSelectImage={targetSelection.selectImage}
                    onSelectStructureSet={targetSelection.selectStructureSet}
                    onSelectRegistration={targetSelection.selectRegistration}
                />

                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <StructureFilterPanel
                        filters={roiSelection.filters}
                        presentTypeGroups={roiSelection.presentTypeGroups}
                        hasActiveFilters={roiSelection.hasActiveFilters}
                        allFilteredSelected={roiSelection.allFilteredSelected}
                        someFilteredSelected={roiSelection.someFilteredSelected}
                        conflictingRoiNumbers={
                            roiSelection.conflictingRoiNumbers
                        }
                        overwriteExisting={overwriteExisting}
                        selectedCount={selectedCount}
                        onToggleFilter={roiSelection.toggleFilter}
                        onToggleTypeGroup={roiSelection.toggleTypeGroup}
                        onIsGroupActive={roiSelection.isGroupActive}
                        onClearFilters={roiSelection.clearFilters}
                        onUpdateSearchText={roiSelection.updateSearchText}
                        onToggleAllFiltered={roiSelection.toggleAllFiltered}
                        onToggleOverwrite={() =>
                            setOverwriteExisting((v) => !v)
                        }
                    />

                    <Divider />

                    <StructureListPanel
                        filteredRois={roiSelection.filteredRois}
                        selectedRois={roiSelection.selectedRois}
                        disabledRoiNumbers={roiSelection.disabledRoiNumbers}
                        conflictingRoiNumbers={
                            roiSelection.conflictingRoiNumbers
                        }
                        overwriteExisting={overwriteExisting}
                        onToggleRoi={roiSelection.toggleRoi}
                    />
                </Box>
            </DialogContent>

            <Divider />

            <DialogActions
                sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}
            >
                <Stack spacing={0.25} sx={{ maxWidth: '60%' }}>
                    {targetSelection.selectedTarget &&
                        !targetSelection.selectedTarget.isSourceImage &&
                        !targetSelection.selectedTarget.geometryMatch &&
                        !targetSelection.selectedTarget.sameFrameOfReference &&
                        !targetSelection.activeRegistration && (
                            <Typography fontSize={11} color="text.secondary">
                                <FormattedMessage
                                    id="structureSet.copy.noRegistrationWarning"
                                    defaultMessage="No registration available for this image."
                                />
                            </Typography>
                        )}
                    {targetSelection.selectedTarget &&
                        (targetSelection.selectedTarget.isSourceImage ||
                            targetSelection.selectedTarget.geometryMatch ||
                            targetSelection.selectedTarget
                                .sameFrameOfReference ||
                            targetSelection.activeRegistration !== null) &&
                        !targetSelection.selectedImageSsUid && (
                            <Typography fontSize={11} color="text.secondary">
                                <FormattedMessage
                                    id="structureSet.copy.noTargetSsWarning"
                                    defaultMessage="Select a structure set to copy into, or choose 'Create new structure set'."
                                />
                            </Typography>
                        )}
                    {roiSelection.selectedRois.size === 0 && (
                        <Typography fontSize={11} color="text.secondary">
                            <FormattedMessage
                                id="structureSet.copy.noRoisSelected"
                                defaultMessage="Select at least one structure to copy."
                            />
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button
                        onClick={onClose}
                        size="small"
                        disabled={isProcessing}
                    >
                        <FormattedMessage id="cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleConfirm}
                        disabled={!canConfirm || isProcessing}
                        data-testid="copy-confirm-button"
                        startIcon={
                            isProcessing ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : undefined
                        }
                    >
                        {isProcessing ? (
                            <FormattedMessage
                                id="structureSet.copy.processing"
                                defaultMessage="Copying..."
                            />
                        ) : (
                            <FormattedMessage
                                id="structureSet.copy.confirm"
                                defaultMessage="Copy ({count})"
                                values={{ count: selectedCount }}
                            />
                        )}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
